import { useState, useEffect, useCallback } from 'react';
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  PASTE_COMMAND,
} from 'lexical';
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { LexicalComposer, type InitialConfigType } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import './LexicalEditor.scss';
import AddFilePlugin from './Plugins/AddFilePlugin';
import { AgxTypography } from '@urbanx/agx.ui-components2';
import AddLinkPlugin from './Plugins/AddLinkPlugin';
import clsx from 'clsx';

const URL_MATCHER =
  /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

const MATCHERS = [
  (text: string) => {
    const match = URL_MATCHER.exec(text);
    if (!match) return null;
    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: fullMatch.startsWith('http') ? fullMatch : `https://${fullMatch}`,
    };
  },
];

interface LexicalEditorProps {
  id: string;
  label?: string;
  defaultValue?: string;
  onChange?: (html: string, text: string) => void;
  maxLength?: number;
  stretch?: boolean;
  placeholder?: string;
  disabled?: boolean;
  ariaDescribedBy?: string;
  invalid?: boolean;
}

const CharacterCountPlugin = ({ onCountChange }: { onCountChange: (count: number) => void }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.getEditorState().read(() => onCountChange($getRoot().getTextContentSize()));
    return editor.registerTextContentListener((text) => onCountChange(text.length));
  }, [editor, onCountChange]);

  return null;
};

const CharacterLimitPlugin = ({ maxLength }: { maxLength: number }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const checkAndHandleInput = (inputData: string, event?: Event) => {
      let shouldBlock = false;

      editor.getEditorState().read(() => {
        const textContent = $getRoot().getTextContent();
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          const selectedLength = selection.getTextContent().length;
          const newLength = textContent.length - selectedLength + inputData.length;

          if (newLength > maxLength) {
            shouldBlock = true;
            const remainingChars = maxLength - (textContent.length - selectedLength);

            if (remainingChars > 0) {
              editor.update(() => {
                const currentSelection = $getSelection();
                if ($isRangeSelection(currentSelection)) {
                  const truncatedInput = inputData.substring(0, remainingChars);
                  currentSelection.insertText(truncatedInput);
                }
              });
            }
          }
        }
      });

      if (shouldBlock && event) {
        event.preventDefault();
        event.stopPropagation();
      }

      return shouldBlock;
    };

    // Handle Enter key
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        checkAndHandleInput('\n', event);
      }
    };

    const handleInput = (event: Event) => {
      const inputEvent = event as InputEvent;
      const clipboardEvent = event as ClipboardEvent;

      let inputData = '';

      switch (event.type) {
        case 'paste':
          inputData = clipboardEvent.clipboardData?.getData('text/plain') || '';
          break;
        case 'beforeinput':
          if (
            inputEvent.inputType === 'insertParagraph' ||
            inputEvent.inputType === 'insertLineBreak'
          ) {
            return;
          }
          inputData = inputEvent.data || '';
          break;
        default:
          return;
      }

      const relevantInputTypes = ['insertText', 'insertCompositionText'];

      if (
        event.type === 'paste' ||
        (event.type === 'beforeinput' && relevantInputTypes.includes(inputEvent.inputType))
      ) {
        checkAndHandleInput(inputData, event);
      }
    };

    const removePasteCommand = editor.registerCommand(
      PASTE_COMMAND,
      (event: Event) => {
        const clipboardEvent = event as ClipboardEvent;
        const clipboardData = clipboardEvent.clipboardData?.getData('text/plain') || '';
        return checkAndHandleInput(clipboardData);
      },
      COMMAND_PRIORITY_HIGH
    );

    const contentEditableElement = editor.getRootElement();
    if (contentEditableElement) {
      contentEditableElement.addEventListener('paste', handleInput, true);
      contentEditableElement.addEventListener('beforeinput', handleInput, true);
      contentEditableElement.addEventListener('keydown', handleKeyDown, true);
    }

    return () => {
      removePasteCommand();
      if (contentEditableElement) {
        contentEditableElement.removeEventListener('paste', handleInput, true);
        contentEditableElement.removeEventListener('beforeinput', handleInput, true);
        contentEditableElement.removeEventListener('keydown', handleKeyDown, true);
      }
    };
  }, [editor, maxLength]);

  return null;
};

const EditablePlugin = ({ disabled }: { disabled: boolean }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => editor.setEditable(!disabled), [disabled, editor]);
  return null;
};

const initialConfig = (defaultValue: string): InitialConfigType => ({
  namespace: 'agx-lexical-editor',
  theme: {
    paragraph: 'agx-lexical-paragraph',
    link: 'agx-lexical-link',
  },
  onError: (error: Error) => {
    console.error('Lexical error:', error);
  },
  nodes: [LinkNode, AutoLinkNode],
  editorState: (editor) => {
    const root = $getRoot();
    if (defaultValue.trim()) {
      const dom = new DOMParser().parseFromString(defaultValue, 'text/html');
      root.append(...$generateNodesFromDOM(editor, dom));
    } else {
      root.append($createParagraphNode());
    }
  },
});

export const LexicalDisplay = ({ html }: { html: string }) => (
  <LexicalComposer initialConfig={{ ...initialConfig(html), editable: false }}>
    <RichTextPlugin
      contentEditable={
        <ContentEditable
          role="generic"
          aria-autocomplete={undefined}
          aria-readonly={undefined}
          tabIndex={-1}
          className="agx-lexical-content-editable"
        />
      }
      placeholder={null}
      ErrorBoundary={(error) => <AgxTypography>Error: {error.children}</AgxTypography>}
    />
  </LexicalComposer>
);

const LexicalEditor = ({
  id,
  label,
  defaultValue = '',
  onChange,
  maxLength = 500,
  stretch = false,
  placeholder = 'Enter content here',
  disabled = false,
  ariaDescribedBy,
  invalid = false,
}: LexicalEditorProps) => {
  const [characterCount, setCharacterCount] = useState(0);

  const handleChange = useCallback(
    (editorState: any, editor: any) => {
      editorState.read(() => {
        const root = $getRoot();
        const textContent = root.getTextContent();

        try {
          const htmlContent = $generateHtmlFromNodes(editor);

          if (onChange) {
            onChange(htmlContent, textContent);
          }
        } catch (error) {
          console.error('Error generating HTML:', error);
          if (onChange) {
            onChange('', textContent);
          }
        }
      });
    },
    [onChange]
  );

  const containerClasses = clsx('agx-lexical-container', stretch && 'agx-lexical-stretch');

  return (
    <div className={containerClasses}>
      {label && (
        <AgxTypography id={`${id}-label`} type="label" size="medium">
          {label}
        </AgxTypography>
      )}
      <LexicalComposer initialConfig={initialConfig(defaultValue)}>
        <div className="agx-lexical-editor-wrapper">
          <div className="agx-lexical-toolbar">
            <AddLinkPlugin
              disabled={disabled || characterCount >= maxLength}
              maxLength={maxLength}
            />
            <AddFilePlugin
              disabled={disabled || characterCount >= maxLength}
              maxLength={maxLength}
            />
          </div>
          <div className="agx-lexical-editor-container">
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  id={id}
                  aria-labelledby={label ? `${id}-label` : undefined}
                  aria-describedby={ariaDescribedBy}
                  aria-invalid={invalid || undefined}
                  className="agx-lexical-content-editable"
                />
              }
              placeholder={<div className="agx-lexical-placeholder">{placeholder}</div>}
              ErrorBoundary={(error) => <div>Error: {error.children}</div>}
            />

            <OnChangePlugin onChange={handleChange} />
            <HistoryPlugin />
            <LinkPlugin />
            <AutoLinkPlugin matchers={MATCHERS} />
            <CharacterCountPlugin onCountChange={setCharacterCount} />
            <EditablePlugin disabled={disabled} />
            {maxLength && <CharacterLimitPlugin maxLength={maxLength} />}
          </div>

          <div className="agx-lexical-character-count">
            <span className={characterCount >= maxLength ? 'agx-lexical-limit-reached' : ''}>
              {maxLength - characterCount} characters left
            </span>
          </div>
        </div>
      </LexicalComposer>
    </div>
  );
};

export default LexicalEditor;
